import type { FirstSessionEntryPoint } from "@/types/firstSessionEntryPoint";
import type { PreparedReferenceImage } from "@/lib/create/referenceImageUploadClient";

export type EntryConversationOptions = {
  /** Durable attribution for the first create-agent turn. */
  entryPoint?: FirstSessionEntryPoint;
  presetType?: string;
  persistAsDesignPrompt?: boolean;
  /** Identity was already established while preparing a direct upload. */
  identityReady?: boolean;
  /** The identity was minted for this first action, so it has no prior dress. */
  identityCreated?: boolean;
  referenceImages?: {
    files: File[];
    urls: string[];
    /** Local compressed previews retained only for a failed first-turn retry. */
    previewUrls?: string[];
  };
};

type EntrySuggestedPrompt = EntryConversationOptions & {
  id: "wedding" | "formal" | "bridesmaid" | "quinceanera" | "prom";
  entryPoint: Extract<
    FirstSessionEntryPoint,
    | "suggested_wedding"
    | "suggested_formal"
    | "suggested_bridesmaid"
    | "suggested_quinceanera"
    | "suggested_prom"
  >;
  message: string;
  presetType: "Wedding" | "Formal" | "Bridesmaid" | "Quinceanera" | "Prom";
};

export const ENTRY_SUGGESTED_PROMPTS: readonly EntrySuggestedPrompt[] = [
  {
    id: "wedding",
    message: "A modern satin wedding dress",
    entryPoint: "suggested_wedding",
    presetType: "Wedding",
  },
  {
    id: "formal",
    message: "A sleek black formal gown",
    entryPoint: "suggested_formal",
    presetType: "Formal",
  },
  {
    id: "bridesmaid",
    message: "A soft blue bridesmaid dress",
    entryPoint: "suggested_bridesmaid",
    presetType: "Bridesmaid",
  },
  {
    id: "quinceanera",
    message: "A sparkling quinceañera ball gown",
    entryPoint: "suggested_quinceanera",
    presetType: "Quinceanera",
  },
  {
    id: "prom",
    message: "A red one-shoulder prom dress",
    entryPoint: "suggested_prom",
    presetType: "Prom",
  },
];

export interface CreateEntryPointProps {
  useBottomInset?: boolean;
  onStartConversation: (
    message: string,
    options?: EntryConversationOptions
  ) => void;
  entryActionPending?: boolean;
  initialPrompt?: string;
  initialReferences?: PreparedReferenceImage[];
  initialPersistAsDesignPrompt?: boolean;
  initialEntryPoint?: FirstSessionEntryPoint;
  initialPresetType?: string;
  entryError?: string | null;
}
